import { useContext } from 'react';
import type { ReactNode } from 'react';

import type { FeatureFlagContextContext } from './types';

export function createFeatureFlagGateComponent<Flags>(
  ldContext: FeatureFlagContextContext<Flags>
) {
  /**
   * SAMPLE USAGE:
   *
   * SomeFeature will be rendered when both ff_1 and ff_2 flags are truthy
   * <FeatureFlagGate predicate={["ff_1", "ff_2"]}>
   *  <SomeFeature />
   * </FeatureFlagGate>
   *
   * SomeFeature will be rendered when either ff_1 or ff_2 flags are truthy
   * <FeatureFlagGate predicate={(flags) => { return flags.ff_1 || flags.ff_2 }}>
   *  <SomeFeature />
   * </FeatureFlagGate>
   *
   * SomeFeature will always be rendered because we've disabled the predicate.
   * <FeatureFlagGate predicate={["ff_1", "ff_2"]} disablePredicate={true}>
   *  <SomeFeature />
   * </FeatureFlagGate>
   *
   */
  const FeatureFlagGate = (props: {
    /**
     * The array signature is meant to be used for simple case of checking multiple boolean flags are true.
     * For anything else use the function signature, don't modify the array signature to be a query engine.
     *
     * If the predicate results in true, children will be rendered.
     * If the predicate results in false, children will not be rendered.
     */
    predicate: FeatureFlagPredicate<Flags>;
    /**
     * Disables checking of predicate above which means the children
     *  will always be displayed.
     *
     * Will only disabled the predicate when resolved to true. Truthy values are not considered.
     */
    disablePredicate?: boolean | FeatureFlagPredicate<Flags> | null;
    fallback?: ReactNode;
    children: ReactNode;
  }) => {
    const context = useContext(ldContext);
    if (!context) {
      throw new Error('FeatureFlagGate can only be used inside its Provider.');
    }

    if (context.loadingState !== 'ready') {
      return null;
    }

    const predicateDisabled =
      props.disablePredicate === undefined || props.disablePredicate === null
        ? false
        : props.disablePredicate === true || props.disablePredicate === false
        ? props.disablePredicate
        : applyFeatureFlagPredicate({
            flags: context.flags,
            predicate: props.disablePredicate,
          });

    if (predicateDisabled) {
      return <>{props.children}</>;
    }

    const featureFlagResult = applyFeatureFlagPredicate({
      flags: context.flags,
      predicate: props.predicate,
    });

    return featureFlagResult ? (
      <>{props.children}</>
    ) : props.fallback ? (
      <>{props.fallback}</>
    ) : null;
  };

  FeatureFlagGate.displayName = 'FeatureFlagGate';

  return FeatureFlagGate;
}

const applyFeatureFlagPredicate = <Flags,>(args: {
  predicate: FeatureFlagPredicate<Flags>;
  flags: Flags;
}) => {
  if (typeof args.predicate === 'function') {
    return args.predicate(args.flags);
  }

  return args.predicate.reduce((acc, flag) => {
    return acc && !!args.flags[flag];
  }, true);
};

type FeatureFlagPredicate<Flags> =
  | Array<keyof Flags>
  | ((flags: Flags) => boolean);
